import { useQuery, useQueryClient } from 'react-query';
import moment from 'moment';
import { authFetch } from '../../CommonUtils/authFetch';
import { urlWithParams } from '../../CommonUtils/urlWithParams';
import { queryKeys } from './queryKeys';
import { ClientAccessData, ClientAccessDTO } from '../ClientAccess.types';
import { OnboardingWorkflow } from '../../CommonSelectors/CommonArea.types';

interface FetchClientAccessParams {
  clientId: string;
  defaultOnboardingWorkflow?: OnboardingWorkflow;
}

const parseClientAccess = (data: ClientAccessDTO): ClientAccessData => {
  return {
    onboardingWorkflow: data.CurrentOnboardingWorkflow,
    dashboardAccess: data.DashboardAccessSet,
    defaultDashboardAccessSet: data.DefaultDashboardAccessSet,
    linkAccountsAccess: data.LinkAccountsAccess,
    dataReconciliationAccess: data.DataReconciliationAccess,
    clientLandingPage: data.ClientLandingPage,
    defaultClientLandingPage: data.DefaultClientLandingPage,
    clientLandingPageOptions: data.ClientLandingPageOptions,
    completedOnboardingMessage:
      data.LastCompletedOnboardingName && data.LastCompletedOnboardingDate
        ? `Your Client completed the ${data.LastCompletedOnboardingName} Workflow on ${moment(
            data.LastCompletedOnboardingDate,
          ).format('MMMM DD, YYYY LT')}`
        : null,
    showProBono: data.ShowProBono,
    isProBonoClient: data.IsProBonoClient,
    taxableAccountModelGroup: data.TaxableAccountModelGroup,
    nonTaxableAccountModelGroup: data.NonTaxableAccountModelGroup,
    fullyAutomatedAccountOpening: data.FullyAutomatedAccountOpening,
    agreementOptions: data.AgreementOptions.map(opt => ({ value: opt.Value, label: opt.Label })),
    selectedAgreements: data.SelectedAgreements,
    isPreviewLinkEnabled: data.IsPreviewLinkEnabled,
    onboardingWorkflowOptions: data.OnboardingWorkflowOptions,
    canAddManualAccount: !!data.CanAddManualAccount,
    displayAddManualAccountToggle: data.DisplayAddManualAccountToggle,
    hasOrionReportsEnabled: data.HasOrionReportsEnabled,
    displayMobileAppPushNotificationsSection:
      data.DisplayMobileAppPushNotificationsSection || false,
    allowMobileAppPushNotifications: data.AllowMobileAppPushNotifications || false,
    defaultDocumentUploadMobilePushNotifications:
      data.DefaultDocumentUploadMobilePushNotifications || false,
    defaultNewsfeedMobilePushNotifications: data.DefaultNewsfeedMobilePushNotifications || false,
    hasDocumentVaultFirm: data.HasDocumentVaultFirm,
    isNewsfeedEnabledOnOC: data.IsNewsfeedEnabledOnOC,
    displayBehavioralFinanceSection: data.DisplayBehavioralFinanceSection,
    hiddenBeFiTools: data.HiddenBeFiTools,
  };
};

const fetchClientAccess = async ({
  clientId,
  defaultOnboardingWorkflow,
}: FetchClientAccessParams): Promise<ClientAccessData> => {
  const data = await authFetch(
    urlWithParams('/Adviser/Adviser/GetClientAccess', { clientId, defaultOnboardingWorkflow }),
  );

  return parseClientAccess(data);
};

interface ClientAccessQueryParams {
  clientId: string;
  defaultOnboardingWorkflow?: OnboardingWorkflow;
}

export const useClientAccessQuery = ({
  clientId,
  defaultOnboardingWorkflow,
}: ClientAccessQueryParams) => {
  return useQuery(
    queryKeys.clientAccess(clientId, defaultOnboardingWorkflow),

    () => fetchClientAccess({ clientId, defaultOnboardingWorkflow }),

    {
      cacheTime: 0,
    },
  );
};

interface CacheParams {
  clientId: string;
  defaultOnboardingWorkflow?: OnboardingWorkflow;
}

export const useClientAccessCache = ({ clientId, defaultOnboardingWorkflow }: CacheParams) => {
  const queryClient = useQueryClient();

  const queryKey = queryKeys.clientAccess(clientId, defaultOnboardingWorkflow);

  const mutateClientAccess = (data: ClientAccessData) => {
    queryClient.setQueryData<ClientAccessData | undefined>(queryKey, old => {
      if (old) {
        return {
          ...old,
          ...data,
        };
      }

      return old;
    });
  };

  return { mutateClientAccess };
};
